import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { QrcodeSvgComponent } from './qrcode-svg/qrcode-svg.component';
import { DatePipe, DecimalPipe } from '@angular/common';
import { TranslatePipe, TranslateDirective, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgModule, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';

import localeSe from '@angular/common/locales/se-SE';
import localeEn from '@angular/common/locales/en-GB';
import localeDe from '@angular/common/locales/de';
import localePl from '@angular/common/locales/pl';
import localeAr from '@angular/common/locales/ar-SY';

registerLocaleData(localeSe, 'se');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeAr, 'ar');

import { AppComponent } from './app.component';
import { LoadingComponent } from './loading/loading.component'
import { NavMenuComponent } from './nav-menu/nav-menu.component';
import { HomeComponent } from './home/home.component';
import { MoreInfoComponent } from './more-info/more-info.component';
import { LoginComponent } from './login/login.component';
import { AdminComponent } from './admin/admin.component';
import { AllaArendenComponent } from './advokat/alla-arenden/alla-arenden.component';
import { MinaTradarComponent } from './advokat/mina-tradar/mina-tradar.component';
import { TradComponent } from './advokat/trad/trad.component';
import { ArendeComponent } from './advokat/arende/arende.component';
import { AnstallningComponent } from './advokat/anstallning/anstallning.component';
import { ArbetstagareComponent } from './arbetstagare/arbetstagare/arbetstagare.component';
import { HuvudB2Component } from './advokat/huvud-b2/huvud-b2.component';
import { ArendeInfoComponent } from './arbetstagare/arende-info/arende-info.component';
import { InstanceComponent } from './instance/instance.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { SignComponent } from './sign/sign.component';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { NotificationModalComponent } from './modal/notification.modal.component';
import { RefsInterceptor } from 'angular-json-refs-interceptor';
import { CreditorLandingComponent } from './creditor/creditor-landing.component';
import { CreditorPublishedFilesComponent } from './creditor/creditor-published-files/creditor-published-files.component';
import { PortalMode } from './enum/portalmode.enum';
import { CreditorClaimFormComponent } from './creditor/creditor-claim-form/creditor-claim-form.component';
import { FieldHelpComponent } from './creditor/creditor-claim-form/field-help/field-help.component';
import { ConfirmLeaveFormModalComponent } from './modal/confirm-leave-form.modal.component';
import { DecimalCommaPipe } from './_pipes/decimal-comma.pipe';
import { DecimalInputComponent } from './decimal-input/decimal-input.component';

import {
  fas,
  faSignOutAlt,
  faWrench,
  faSignInAlt,
  faListUl,
  faPlay,
  faKey,
  faAt,
  faFont,
  faPassport,
  faExclamationTriangle,
  faTimesCircle,
  faCheck,
  faAddressBook,
  faPhone,
  faCreditCard,
  faSpinner,
  faLaptop,
  faMobileAlt,
  faCoins,
  faCalendarAlt,
  faGavel,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFile,
  faArrowDown,
  faSortAmountDown,
  faSortAmountUp,
  faArrowLeft,
  faFilter,
  faSearch,
  faUser,
  faCity,
  faCheckCircle,
  faMarker,
  faPenNib,
  faMale,
  faFemale,
  faInfo,
  faGlobe,
  faQrcode,
  faSignature,
  faBan,
  faAward,
  faCopy,
  faCircle,
  faArrowCircleUp,
  faCog,
  faIdBadge,
  faKeyboard,
  faCircleQuestion,
  faReply,
  faPaperclip
} from '@fortawesome/free-solid-svg-icons';

import {
  far,
  faSmile,
  faFrown
} from '@fortawesome/free-regular-svg-icons';

// Egna ikoner
import {
  faBid,
  faLst,
  faSkv
} from './_icons/custom-icons';

import { NgxSpinnerModule } from 'ngx-spinner';
import { AnimatedEllipsisComponent } from './animated-ellipsis/animated-ellipsis.component';
import { FilterPipeModule } from 'ngx-filter-pipe';
import { OrderModule } from 'ngx-order-pipe';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CustomHttpInterceptor } from './interceptor/custom.interceptor';

import { AuthGuard, AuthGuardProvider } from './auth/auth.guard';
import { creditorClaimFormGuard } from './creditor/creditor-claim-form/creditor-claim-form.guard';
import { AuthService } from './auth/auth.service';
import { SessionService } from './session/session.service';
import { FileService } from './file/file.service';
import { ErrorModalService } from './modal/error.modal.service';
import { ArbetstagareService } from './arbetstagare/arbetstagare/arbetstagare.service';
import { ArendeInfoService } from './arbetstagare/arende-info/arende-info.service';
import { AllaArendenService } from './advokat/alla-arenden/alla-arenden.service';
import { CreditorService } from './creditor/creditor.service';
import { ArendeService } from './advokat/arende/arende.service';
import { MinaTradarService } from './advokat/mina-tradar/mina-tradar.service';
import { TradService } from './advokat/trad/trad.service';
import { AnstallningService } from './advokat/anstallning/anstallning.service';
import { HuvudB2Service } from './advokat/huvud-b2/huvud-b2.service';
import { AttestService } from './attest/attest.service';
import { IndividualB2LinkService } from './individual-b2-link/individual-b2-link.service';
import { MinaMeddelandenService } from './mina-meddelanden/mina-meddelanden.service';
import { NavMenuService } from './nav-menu/nav-menu.service';
import { ArbetstagareIconComponent } from './arbetstagare-icon/arbetstagare-icon.component';
import { BeslutIconComponent } from './beslut-icon/beslut-icon.component';
import { SignatureIconComponent } from './signature-icon/signature-icon.component';
import { IndividualB2LinkComponent } from './individual-b2-link/individual-b2-link.component';
import { FileLinkComponent } from './file-link/file-link.component';
import { FileLinkFromGkComponent } from './file-link/file-link-fromgk.component';
import { NgbdErrorModal } from './modal/error.modal.component';
import { NgbdMessageModal } from './modal/message.modal.component';
import { NgbdLogoutModal } from './logout/logout.modal.component';
import { SettingsComponent } from './arbetstagare/settings/settings.component';
import { NgbdBankIdModal } from './modal/bank-id.modal.component';
import { NgbdOtpModal } from './modal/otp.modal.component';
import { NgbdOtpValidateModal } from './modal/otp-validate.modal.component';
import { NgbdOtpSignModal } from './modal/otp-sign.modal.component';
import { NgbdB2BilagorModal } from './modal/b2-bilagor.modal.component';
import { NgbdSwitchLanguageModal } from './modal/switch-language.modal.component';
import { LocalizedDatePipe } from './_pipes/localized-date.pipe';
import { LocalizedDecimalPipe } from './_pipes/localized-decimal.pipe';
import { UpperFirstPipe } from './_pipes/upper-first.pipe';
import { JoinPipe } from './_pipes/join.pipe';
import { SafeHtmlPipe } from './_pipes/safe-html.pipe';
import { YesNoModalService } from './modal/yes-no.modal.service';
import { NgbdYesNoModal } from './modal/yes-no.modal.component';
import { MinaMeddelandenComponent } from './mina-meddelanden/mina-meddelanden.component';
import { InfoRequestComponent } from './info-request/info-request.component';
import { InfoRequestService } from './info-request/info-request.service';
import { FileUploadComponent } from './file/fileUpload/file-upload/file-upload.component';
import { SharedStateProvider } from './sharedStateProvider/shared-state-provider';
import { CreditorCaseComponent } from './creditor/creditor-case/creditor-case.component';
import { ClaimTypeLabelPipe } from './_pipes/claim-type-label.pipe';
import { CreditorTypeLabelPipe } from './_pipes/creditor-type-label.pipe';
import { AccountTypeLabelPipe } from './_pipes/account-type-label.pipe';
import { PreferentialRightGroupLabelPipe } from './_pipes/preferential-right-group-label.pipe';
import { PreferentialRightNamePipe } from './_pipes/preferential-right-name.pipe';
import { CreditorRemarkComponent } from './creditor/creditor-remark/creditor-remark.component';
import { CreditorRemarkService } from './creditor/creditor-remark/creditor-remark.service';
import { CreditorGroupComponent } from './creditor/creditor-group/creditor-group.component';
import { CreditorGroupAcceptComponent } from './creditor/creditor-group-accept/creditor-group-accept.component';
import { CreditorGroupTransferModalComponent } from './creditor/creditor-group-transfer-modal/creditor-group-transfer-modal.component';

@NgModule({ declarations: [
        AppComponent,
        AnimatedEllipsisComponent,
        LoadingComponent,
        NavMenuComponent,
        HomeComponent,
        MoreInfoComponent,
        NotFoundComponent,
        AdminComponent,
        LoginComponent,
        ArbetstagareComponent,
        AllaArendenComponent,
        MinaTradarComponent,
        TradComponent,
        ArendeComponent,
        AnstallningComponent,
        HuvudB2Component,
        InstanceComponent,
        SettingsComponent,
        ArendeInfoComponent,
        ArbetstagareIconComponent,
        BeslutIconComponent,
        SignatureIconComponent,
        IndividualB2LinkComponent,
        SignComponent,
        FileLinkComponent,
        FileLinkFromGkComponent,
        NgbdYesNoModal,
        NgbdErrorModal,
        NgbdMessageModal,
        NgbdLogoutModal,
        NgbdBankIdModal,
        NgbdOtpModal,
        NgbdOtpValidateModal,
        NgbdOtpSignModal,
        NgbdB2BilagorModal,
        NgbdSwitchLanguageModal,
        LocalizedDatePipe,
        LocalizedDecimalPipe,
        DecimalCommaPipe,
        DecimalInputComponent,
        UpperFirstPipe,
        JoinPipe,
        SafeHtmlPipe,
        NotificationModalComponent,
        MinaMeddelandenComponent,
        InfoRequestComponent,
        FileUploadComponent,
        CreditorLandingComponent,
        CreditorCaseComponent,
        CreditorClaimFormComponent,
        FieldHelpComponent,
        CreditorPublishedFilesComponent,
        ClaimTypeLabelPipe,
        CreditorTypeLabelPipe,
        AccountTypeLabelPipe,
        PreferentialRightGroupLabelPipe,
        PreferentialRightNamePipe,
        ConfirmLeaveFormModalComponent,
        PreferentialRightNamePipe,
        CreditorRemarkComponent,
        CreditorGroupComponent,
        CreditorGroupAcceptComponent,
        CreditorGroupTransferModalComponent,
        QrcodeSvgComponent
    ],
    bootstrap: [AppComponent], imports: [NgbModule,
        NgbCollapseModule,
        BrowserModule,
        BrowserAnimationsModule,
        FontAwesomeModule,
        NgxSpinnerModule,
        FilterPipeModule,
        OrderModule,
        FormsModule,
        ReactiveFormsModule,
        TranslatePipe,
        TranslateDirective,
        RouterModule.forRoot([
            { path: 'home', component: HomeComponent, data: { state: 'home' } },
            { path: 'more-info', component: MoreInfoComponent, data: { state: 'more-info', textKey: 'more-info.brodtext', borgenarTextKey: 'more-info.brodtext-borgenar' } },
            { path: 'personal-data', component: MoreInfoComponent, data: { state: 'personal-data', textKey: 'personal-data.brodtext', borgenarTextKey: 'personal-data.brodtext-borgenar' } },
            { path: 'login', component: LoginComponent, data: { state: 'login' } },
            { path: 'admin-kupxxy', component: AdminComponent, data: { state: 'login' } },
            { path: 'borgenar', component: LoginComponent, data: { state: 'login', loginMode: PortalMode.Borgenar } },
            { path: 'borgenar/start', component: CreditorLandingComponent, canActivate: [AuthGuard], data: { state: 'borgenar' } },
            { path: 'borgenar/arende/:id/publicerade-filer', component: CreditorPublishedFilesComponent, canActivate: [AuthGuard], data: { state: 'borgenar-publicerade-filer' } },
            { path: 'borgenar/arende/:id', component: CreditorCaseComponent, canActivate: [AuthGuard], data: { state: 'borgenar-arende' } },
            { path: 'borgenar/arende/:id/anmalan/ny', component: CreditorClaimFormComponent, canActivate: [AuthGuard], canDeactivate: [creditorClaimFormGuard], data: { state: 'borgenar-anmalan-ny' } },
            { path: 'borgenar/arende/:id/anmalan/:claimId', component: CreditorClaimFormComponent, canActivate: [AuthGuard], canDeactivate: [creditorClaimFormGuard], data: { state: 'borgenar-anmalan' } },
            { path: 'borgenar/arende/:id/bevakning/ny', component: CreditorClaimFormComponent, canActivate: [AuthGuard], canDeactivate: [creditorClaimFormGuard], data: { state: 'borgenar-bevakning-ny' } },
            { path: 'borgenar/arende/:id/bevakning/ny/:claimId', component: CreditorClaimFormComponent, canActivate: [AuthGuard], canDeactivate: [creditorClaimFormGuard], data: { state: 'borgenar-bevakning-ny' } },
            { path: 'borgenar/arende/:id/efterbevakning/ny', component: CreditorClaimFormComponent, canActivate: [AuthGuard], canDeactivate: [creditorClaimFormGuard], data: { state: 'borgenar-efterbevakning-ny' } },
            { path: 'borgenar/arende/:id/efterbevakning/ny/:claimId', component: CreditorClaimFormComponent, canActivate: [AuthGuard], canDeactivate: [creditorClaimFormGuard], data: { state: 'borgenar-efterbevakning-ny' } },
            { path: 'borgenar/arende/:id/bevakning/:claimId', component: CreditorClaimFormComponent, canActivate: [AuthGuard], canDeactivate: [creditorClaimFormGuard], data: { state: 'borgenar-bevakning' } },
            { path: 'borgenar/group', component: CreditorGroupComponent, canActivate: [AuthGuard], data: { state: 'borgenar-group' } },
            { path: 'borgenar/group/accept/:token', component: CreditorGroupAcceptComponent, data: { state: 'borgenar-group-accept' } },
            { path: 'settings', component: SettingsComponent, canActivate: [AuthGuard], data: { state: 'settings' } },
            { path: 'mina-arenden', component: AllaArendenComponent, canActivate: [AuthGuard], data: { state: 'alla-arenden' } },
            { path: 'mina-arenden/:id', component: ArendeComponent, canActivate: [AuthGuard], data: { state: 'arende' } },
            { path: 'mina-tradar', component: MinaTradarComponent, canActivate: [AuthGuard], data: { state: 'mina-tradar' } },
            { path: 'mina-tradar/:id', component: TradComponent, canActivate: [AuthGuard], data: { state: 'trad' } },
            { path: 'mina-meddelanden', component: MinaMeddelandenComponent, canActivate: [AuthGuard], data: { state: 'mina-meddelanden' } },
            { path: 'mina-meddelanden/:id', component: InfoRequestComponent, canActivate: [AuthGuard], data: { state: 'info-request' } },
            { path: 'anstallning/:id', component: AnstallningComponent, canActivate: [AuthGuard], data: { state: 'anstallning' } },
            { path: 'beslut', component: ArbetstagareComponent, canActivate: [AuthGuard], data: { state: 'beslut' } },
            { path: 'huvud-b2/:id', component: HuvudB2Component, canActivate: [AuthGuard], data: { state: 'huvud-b2' } },
            { path: 'ovrig-info', component: ArendeInfoComponent, canActivate: [AuthGuard], data: { state: 'ovrig-info' } },
            { path: 'select-instance', component: InstanceComponent, canActivate: [AuthGuard], data: { state: 'select-instance' } },
            { path: '', component: LoadingComponent, data: { state: '' }, pathMatch: 'full' },
            { path: '**', component: NotFoundComponent, data: { state: 'not-found' } }
        ])], providers: [
        provideTranslateService({
            fallbackLang: 'se',
            loader: provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: '.json' })
        }),
        DatePipe,
        DecimalPipe,
        AuthService,
        SessionService,
        FileService,
        ArbetstagareService,
        ArendeInfoService,
        AllaArendenService,
        CreditorService,
        ArendeService,
        MinaTradarService,
        TradService,
        AnstallningService,
        HuvudB2Service,
        YesNoModalService,
        ErrorModalService,
        AttestService,
        IndividualB2LinkService,
        MinaMeddelandenService,
        InfoRequestService,
        NavMenuService,
        CreditorRemarkService,
        AuthGuardProvider,
        SharedStateProvider,
        NavMenuService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CustomHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RefsInterceptor,
            multi: true
        },
        {
            provide: LOCALE_ID,
            useValue: 'se'
        },
        provideHttpClient(withXhr(), withInterceptorsFromDi())
    ] })
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIconPacks(fas, far);
    library.addIcons(
      faSignOutAlt,
      faWrench,
      faSignInAlt,
      faListUl,
      faPlay,
      faKey,
      faAt,
      faFont,
      faPassport,
      faExclamationTriangle,
      faTimesCircle,
      faCheck,
      faAddressBook,
      faPhone,
      faCreditCard,
      faSpinner,
      faLaptop,
      faMobileAlt,
      faCoins,
      faCalendarAlt,
      faGavel,
      faFilePdf,
      faFileWord,
      faFileExcel,
      faFile,
      faArrowDown,
      faSortAmountDown,
      faSortAmountUp,
      faArrowLeft,
      faFilter,
      faSearch,
      faUser,
      faCity,
      faCheckCircle,
      faMarker,
      faPenNib,
      faSmile,
      faFrown,
      faMale,
      faFemale,
      faInfo,
      faGlobe,
      faQrcode,
      faSignature,
      faBan,
      faAward,
      faCopy,
      faCircle,
      faArrowCircleUp,
      faCog,
      faIdBadge,
      faKeyboard,
      faCircleQuestion,
      faReply,
      faPaperclip,

      //Egna ikoner
      faBid,
      faLst,
      faSkv);
  }
}
